import React from 'react';

/**
 * TenantPicker - Dropdown select component for choosing a tenant.
 *
 * Props:
 * @param {Array<{ TenantID: string, FriendlyName: string, Domain?: string }>} tenants
 * @param {string} value - Currently selected TenantID
 * @param {function} onChange - Callback when selection changes, receives TenantID string
 * @param {boolean} [disabled] - Whether the picker is disabled
 * @param {boolean} [loading] - Whether tenants are still loading
 * @param {string} [error] - Error message to display
 *
 * Example tenants array:
 * [
 *   { TenantID: "abc-123", FriendlyName: "Contoso Corp", Domain: "contoso.com" },
 *   { TenantID: "def-456", FriendlyName: "Fabrikam Inc", Domain: "fabrikam.com" }
 * ]
 */
export default function TenantPicker({ tenants = [], value, onChange, disabled, loading, error }) {
  return (
    <div>
      <label
        htmlFor="tenant-picker"
        className="block text-sm font-medium text-gray-300 mb-1.5"
      >
        Tenant
      </label>
      <div className="relative">
        <select
          id="tenant-picker"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled || loading}
          className="input-field appearance-none pr-10 cursor-pointer disabled:cursor-not-allowed"
        >
          <option value="" disabled>
            {loading ? 'Loading tenants...' : 'Select a tenant'}
          </option>
          {[...tenants].sort((a, b) => (a.FriendlyName || '').localeCompare(b.FriendlyName || '')).map((tenant) => (
            <option key={tenant.TenantID} value={tenant.TenantID}>
              {tenant.FriendlyName}
              {tenant.Domain ? ` (${tenant.Domain})` : ''}
              {/* Retained (2026-09-29): collection off, logs kept — investigable, but no new data arrives. */}
              {tenant.Retained ? ' — retained, no new logs' : ''}
            </option>
          ))}
        </select>

        {/* Dropdown arrow indicator */}
        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
          {loading ? (
            <svg
              className="animate-spin h-4 w-4 text-gray-500"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
              />
            </svg>
          ) : (
            <svg
              className="h-4 w-4 text-gray-500"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                clipRule="evenodd"
              />
            </svg>
          )}
        </div>
      </div>

      {error && (
        <p className="mt-1.5 text-sm text-red-400">{error}</p>
      )}

      {!loading && tenants.length === 0 && !error && (
        <p className="mt-1.5 text-sm text-gray-500">
          No tenants available. Please configure tenants in the backend.
        </p>
      )}
    </div>
  );
}
